import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useCompany } from '../../../hooks/useCompany';

const CompanyForm = () => {
  // Estilo para botones de acción principal
  const actionButtonStyle = {
    borderRadius: '7px',
    color: 'white',
    fontWeight: 600,
    padding: '7px 14px',
    textTransform: 'none',
    backgroundImage: 'linear-gradient(to right, #F5A623 0%, #D9890D 100%)',
    boxShadow: '0 4px 15px rgba(245, 166, 35, 0.14)',
    transition: 'all 0.2s ease-in-out',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(245, 166, 35, 0.18)',
      backgroundImage: 'linear-gradient(to right, #F5A623 0%, #D9890D 100%)',
      backgroundColor: 'transparent',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 10px rgba(245, 166, 35, 0.14)',
    },
    '&.Mui-disabled': {
      backgroundImage: 'linear-gradient(to right, #919191 0%, #b7b7b7 100%)',
      color: 'rgba(255, 255, 255, 0.6)',
    }
  };

  const { company, loading, error: apiError, saveCompany } = useCompany();
  const [formData, setFormData] = useState({
    nombre: '',
    rif: '',
    direccion: '',
    ciudad: '',
    estado: '',
    telefono: '',
    email: '',
    esContribuyenteEspecial: false
  });
  const [saveStatus, setSaveStatus] = useState({ success: false, message: '' });

  useEffect(() => {
    if (company) {
      setFormData(company);
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await saveCompany(formData);
      setSaveStatus({
        success: true,
        message: 'Información de la empresa actualizada exitosamente'
      });
    } catch (err) {
      setSaveStatus({
        success: false,
        message: 'Error al actualizar la información de la empresa'
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Información General
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre de la Empresa"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="RIF"
              name="rif"
              value={formData.rif}
              onChange={handleChange}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          {/* La condición de contribuyente especial decide si la empresa percibe
              IGTF en los pagos en divisa. No es un detalle cosmético: cambia el
              total de toda factura en moneda extranjera. */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.esContribuyenteEspecial}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    esContribuyenteEspecial: e.target.checked
                  }))}
                  color="warning"
                />
              }
              label="Contribuyente especial"
            />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', ml: 6, mt: -0.5 }}>
              Si el SENIAT designó a la empresa como contribuyente especial, sus
              facturas en divisas cobran el IGTF del 3%.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Dirección
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Dirección"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ciudad"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Estado"
              name="estado"
              value={formData.estado}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Contacto
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Teléfono"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }
              }}
            />
          </Grid>

          {(apiError || saveStatus.message) && (
            <Grid item xs={12}>
              <Alert severity={saveStatus.success ? "success" : "error"}>
                {apiError || saveStatus.message}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            {/*
              Acá había un botón «Eliminar Información» que llamaba a
              DELETE /api/company. Se sacó: la baja de una empresa la decide la
              plataforma, no el cliente desde su propia configuración.

              Nunca llegó a funcionar —apuntaba a http://localhost:5002 y no
              mandaba el token— y eso fue una suerte: el back hace un
              Company.deleteOne() que no toca usuarios, facturas ni clientes,
              así que un click dejaba las facturas huérfanas y sin vuelta atrás.

              La ruta del back sigue existiendo. Si algún día hace falta dar de
              baja una empresa, va por `active: false` (lo sugiere el propio
              company.service.js) y desde el área de plataforma.
            */}
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ ...actionButtonStyle }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'GUARDAR CAMBIOS'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CompanyForm;